import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft, Edit3, Plus, MapPin, Phone, Mail, Calendar, Trash2,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { PageHeader, Spinner, EmptyState } from "../components/UI";
import StageBadge from "../components/StageBadge";
import { fmtDate } from "../lib/utils";

export default function EstablishmentDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [estab, setEstab] = useState(null);
  const [visits, setVisits] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [e, v, b] = await Promise.all([
          api.get(`/establishments/${id}`),
          api.get("/visits", { params: { establishment_id: id } }),
          api.get("/brands"),
        ]);
        setEstab(e.data);
        setVisits(v.data.items || []);
        setBrands(b.data.items || []);
      } catch (err) {
        toast.error(formatApiError(err.response?.data?.detail) || err.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const remove = async () => {
    if (!window.confirm("Delete this establishment and all its visits?")) return;
    try {
      await api.delete(`/establishments/${id}`);
      toast.success("Establishment deleted");
      navigate("/establishments");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  if (loading) return <Spinner />;
  if (!estab) return <div className="text-stage-lostFg">Not found</div>;

  const brandsForEstab = brands.filter((b) => estab.brand_ids?.includes(b.id));

  return (
    <div>
      <Link to="/establishments" className="btn-ghost mb-2" data-testid="back-to-list">
        <ArrowLeft size={16} /> All establishments
      </Link>

      <div className="card p-6 mb-4">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="font-display text-3xl text-brand-ink">{estab.name}</h1>
              <StageBadge stage={estab.pipeline_stage} />
            </div>
            <div className="text-sm text-brand-mutedFg mt-1 capitalize">{estab.type.replace("_", " ")}</div>
            <div className="mt-4 space-y-2 text-sm">
              {(estab.address || estab.city) && (
                <div className="flex items-center gap-2 text-brand-ink2">
                  <MapPin size={14} className="text-brand-mutedFg" />
                  <span>{[estab.address, estab.city, estab.state, estab.zip_code].filter(Boolean).join(", ")}</span>
                </div>
              )}
              {estab.contact_phone && (
                <div className="flex items-center gap-2 text-brand-ink2">
                  <Phone size={14} className="text-brand-mutedFg" />
                  <a href={`tel:${estab.contact_phone}`} className="hover:underline">{estab.contact_phone}</a>
                </div>
              )}
              {estab.contact_email && (
                <div className="flex items-center gap-2 text-brand-ink2">
                  <Mail size={14} className="text-brand-mutedFg" />
                  <a href={`mailto:${estab.contact_email}`} className="hover:underline">{estab.contact_email}</a>
                </div>
              )}
              {estab.contact_name && (
                <div className="text-brand-mutedFg">Contact: <span className="text-brand-ink2">{estab.contact_name}</span></div>
              )}
            </div>
            {estab.notes && (
              <div className="mt-4">
                <div className="label">Notes</div>
                <div className="text-sm text-brand-ink2 mt-1 whitespace-pre-wrap">{estab.notes}</div>
              </div>
            )}
            {brandsForEstab.length > 0 && (
              <div className="mt-4">
                <div className="label">Pitching</div>
                <div className="flex flex-wrap gap-2 mt-1">
                  {brandsForEstab.map((b) => (
                    <span key={b.id} className="badge bg-brand-muted text-brand-ink">{b.name}</span>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div className="flex sm:flex-col gap-2">
            <Link to={`/visits/new?establishment_id=${id}`} className="btn-primary" data-testid="log-visit-button">
              <Plus size={16} /> Log visit
            </Link>
            <Link to={`/establishments/${id}/edit`} className="btn-outline" data-testid="edit-establishment-button">
              <Edit3 size={16} /> Edit
            </Link>
            <button onClick={remove} className="btn-outline text-stage-lostFg" data-testid="delete-from-detail">
              <Trash2 size={16} /> Delete
            </button>
          </div>
        </div>
      </div>

      <div className="card p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="label">History</div>
            <h2 className="font-display text-2xl text-brand-ink">Visit timeline</h2>
          </div>
          <Link to={`/visits/new?establishment_id=${id}`} className="btn-ghost text-sm">
            <Plus size={14} /> New visit
          </Link>
        </div>

        {visits.length === 0 ? (
          <EmptyState
            testid="empty-visits-detail"
            icon={Calendar}
            title="No visits logged"
            body="Drop a note after your next stop to start the timeline."
            action={<Link to={`/visits/new?establishment_id=${id}`} className="btn-primary"><Plus size={16}/> Log visit</Link>}
          />
        ) : (
          <ol className="relative border-l border-brand-border pl-6 space-y-6" data-testid="visits-timeline">
            {visits.map((v) => (
              <li key={v.id} data-testid={`visit-${v.id}`}>
                <span className="absolute -left-[7px] mt-1.5 w-3.5 h-3.5 rounded-full bg-brand-ink ring-4 ring-brand-bg"></span>
                <div className="text-xs text-brand-mutedFg">{fmtDate(v.visit_date)}</div>
                {v.outcome && (
                  <div className="badge stage-qualified mt-1">{v.outcome}</div>
                )}
                <p className="mt-2 text-brand-ink2 whitespace-pre-wrap">{v.notes}</p>
                {v.photos?.length > 0 && (
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mt-3">
                    {v.photos.map((p, i) => (
                      <a key={`${v.id}-photo-${i}`} href={p} target="_blank" rel="noreferrer">
                        <img src={p} alt={`Visit photo ${i+1}`} className="w-full h-24 object-cover rounded-xl border border-brand-border" />
                      </a>
                    ))}
                  </div>
                )}
              </li>
            ))}
          </ol>
        )}
      </div>
    </div>
  );
}
